import { ChargeWaiverConstant } from "presentation/constant/ChargeWaiver/ChargeWaiverConstant";
import { discountTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { useChargeWaiverVM } from "presentation/hook/ChargeWaiver/useChargeWaiverVM";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useChargeWaiverTracked } from "presentation/store/ChargeWaiver/ChargeWaiverProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import { useMemo } from "react";
import { FieldType, IFieldValue } from "veronica-ui-component/dist/component/core";


export const ChargeWaiverEditForm = () => {
  const CHARGE_WAIVER_CONSTANT = ChargeWaiverConstant.Edit;
  const [chargeWaiverState] = useChargeWaiverTracked();
  const chargeWaiverVM = useChargeWaiverVM();
  const { editData, isShowDelayDelivery, isShowGenWaiverByCom, isShowRolloverChg, isShowStackHold, isShowBulkEditPanel, isShowDelayGrding, isApproval, masterState, selectedRows } = chargeWaiverState;
  const { allFormState, isSaveClicked } = masterState
  const [anaInfoState] = useANAInfoTracked();
  const { operatingUnit } = anaInfoState;

  // Bulk edit: a field is editable only when at least one selected row's reason would
  // persist it (aligned with legacy back-end reason-code branches)
  const isFieldEditable = (field: 'batNo' | 'remark1' | 'reasonForRecommendation') => {
    if (!isShowBulkEditPanel) return true;
    const reasons = (selectedRows || []).map(row => row.waiverReason);
    if (field === 'batNo') return reasons.some(reason => reason === 'DELAY_DELIVERY');
    if (field === 'remark1') return reasons.some(reason => reason === 'ROLLOVER' || reason === 'STUCK_MISSING_STACK_HOLD');
    if (field === 'reasonForRecommendation') return reasons.some(reason => reason === 'GENERAL_WAIVER');
    return true;
  };

  const memoWaiverPercentage = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={isApproval}
        isShowOptional={isShowBulkEditPanel}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.waiverPercentage?.toString() || ''}
        fieldValue={editData?.waiverPercentage?.toString()}
        fieldLabel={CHARGE_WAIVER_CONSTANT.WAIVE}
        fieldType={FieldType.TEXT}
        fieldKey={'waiverPercentage'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [allFormState, isApproval, isSaveClicked, editData?.waiverPercentage, CHARGE_WAIVER_CONSTANT.WAIVE, chargeWaiverVM, isShowBulkEditPanel])

  const memoBatNo = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={isApproval}
        isShowOptional={isShowBulkEditPanel}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.batNo || ''}
        fieldValue={editData?.batNo}
        fieldLabel={CHARGE_WAIVER_CONSTANT.BAT_NO}
        fieldType={FieldType.TEXT}
        fieldKey={'batNo'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        makeDisabled={!isFieldEditable('batNo')}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [allFormState, isApproval, isSaveClicked, editData?.batNo, CHARGE_WAIVER_CONSTANT.BAT_NO, chargeWaiverVM, isShowBulkEditPanel, selectedRows])

  const memoWaiverNoOfDays = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={isApproval}
        isShowOptional={isShowBulkEditPanel}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.waiverNoOfDays?.toString() || ''}
        fieldValue={editData?.waiverNoOfDays?.toString()}
        fieldLabel={CHARGE_WAIVER_CONSTANT.NO_OF_WAIVED_DAYS}
        fieldType={FieldType.TEXT}
        fieldKey={'waiverNoOfDays'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [allFormState, isApproval, isSaveClicked, editData?.waiverNoOfDays, CHARGE_WAIVER_CONSTANT.NO_OF_WAIVED_DAYS, chargeWaiverVM, isShowBulkEditPanel])

  const memoRemark1 = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={false}
        isShowOptional={isShowBulkEditPanel}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.remark1 || ''}
        fieldValue={editData?.remark1}
        fieldLabel={CHARGE_WAIVER_CONSTANT.REMARK1}
        fieldType={FieldType.TEXT}
        fieldKey={'remark1'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        makeDisabled={!isFieldEditable('remark1')}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [allFormState, isSaveClicked, editData?.remark1, CHARGE_WAIVER_CONSTANT.REMARK1, chargeWaiverVM, isShowBulkEditPanel, selectedRows])

  const memoRemark2 = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={{}}
        isReadOnly={false}
        isShowOptional={operatingUnit !== "SAPT" && operatingUnit !== "KICT"}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.remark2 || ''}
        fieldValue={editData?.remark2}
        fieldLabel={CHARGE_WAIVER_CONSTANT.REMARK2}
        fieldType={FieldType.TEXT}
        fieldKey={'remark2'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [isSaveClicked, editData?.remark2, CHARGE_WAIVER_CONSTANT.REMARK2, chargeWaiverVM, operatingUnit])

  /*const memoChargeDateFromTo = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <DateRangePickerComponent
        label={CHARGE_WAIVER_CONSTANT.CHARGING_DATE_RANGE}
        width='280px'
        fieldNames={{ startField: "chrfrmDate", endField: "chgToDate" }}
        dateRange={{ startDate: editData.chrfrmDate, endDate: editData.chgToDate }}
        onDatesChange={chargeWaiverVM.onDateRangeChange}
      />
    </div>
    , [CHARGE_WAIVER_CONSTANT.CHARGING_DATE_RANGE, editData.chrfrmDate, editData.chgToDate, chargeWaiverVM.onDateRangeChange])*/

  const memoChargeDateFrom = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisDateTimePickerComponent
        disabled={editData?.allowUpdateFromDate !== "Y"}
        optional={false}
        label={CHARGE_WAIVER_CONSTANT.CHARGING_DATE_FROM}
        width="280px"
        date={editData?.chrfrmDate}
        fieldName="chrfrmDate"
        errorMessage={allFormState?.chrfrmDate}
        onDateChange={chargeWaiverVM.onEditFieldChange} />
    </div>
    , [allFormState, CHARGE_WAIVER_CONSTANT.CHARGING_DATE_FROM, editData?.chrfrmDate, editData?.allowUpdateFromDate, chargeWaiverVM])

  const memoChargeDateTo = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisDateTimePickerComponent
        disabled={editData?.allowUpdateToDate !== "Y"}
        optional={false}
        label={CHARGE_WAIVER_CONSTANT.CHARGING_DATE_TO}
        width="280px"
        date={editData?.chgToDate}
        fieldName="chgToDate"
        errorMessage={allFormState?.chgToDate}
        onDateChange={chargeWaiverVM.onEditFieldChange} />
    </div>
    , [allFormState, CHARGE_WAIVER_CONSTANT.CHARGING_DATE_TO, editData?.chgToDate, editData?.allowUpdateToDate, chargeWaiverVM])


  const memoReasonForRecommendation = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={isApproval}
        isShowOptional={isShowBulkEditPanel}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.reasonForRecommendation || ''}
        fieldValue={editData?.reasonForRecommendation}
        fieldLabel={CHARGE_WAIVER_CONSTANT.REASON_FOR_RECOMMENDATION}
        fieldType={FieldType.TEXT}
        fieldKey={'reasonForRecommendation'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        makeDisabled={!isFieldEditable('reasonForRecommendation')}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [allFormState, isApproval, isSaveClicked, editData?.reasonForRecommendation, CHARGE_WAIVER_CONSTANT.REASON_FOR_RECOMMENDATION, chargeWaiverVM, isShowBulkEditPanel, selectedRows])

  const memoWaiverType = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={allFormState}
        isReadOnly={isApproval}
        isShowOptional={false}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.waiverType || ''}
        fieldValue={editData?.waiverType}
        fieldLabel={CHARGE_WAIVER_CONSTANT.WAIVE_TYPE}
        fieldType={FieldType.DROPDOWN}
        fieldKey={'waiverType'}
        maxLength={60}
        options={discountTypeDropdownOption}
        requiredFieldList={[]}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [CHARGE_WAIVER_CONSTANT.WAIVE_TYPE, allFormState, chargeWaiverVM, editData?.waiverType, isApproval, isSaveClicked])

  const memoRemark = useMemo(() =>
    <div className='im-flex-row-item' style={{ width: "280px", marginBottom: "12px" }}>
      <NbisCommonField
        errorMessages={{}}
        isReadOnly={false}
        isShowOptional={true}
        isSaveClicked={isSaveClicked}
        readOnlyValue={editData?.remark || ''}
        fieldValue={editData?.remark}
        fieldLabel={CHARGE_WAIVER_CONSTANT.REMARKS}
        fieldType={FieldType.TEXT}
        fieldKey={'remark'}
        isShowMissingError={true}
        maxLength={20}
        requiredFieldList={[]}
        onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
          chargeWaiverVM.onEditFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
    </div>
    , [isSaveClicked, editData?.remark, CHARGE_WAIVER_CONSTANT.REMARKS, chargeWaiverVM])


  return <>
    <CriteriaItemContainer>
      {((operatingUnit === "SAPT" || operatingUnit === "KICT") || (operatingUnit !== "SAPT" && operatingUnit !== "KICT" && isShowDelayGrding)) && memoWaiverNoOfDays}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(isShowDelayDelivery || isShowBulkEditPanel) && memoBatNo}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(operatingUnit !== "SAPT" && operatingUnit !== "KICT" && !isShowDelayGrding) && memoChargeDateFrom}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(operatingUnit !== "SAPT" && operatingUnit !== "KICT" && !isShowDelayGrding) && memoChargeDateTo}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(isShowStackHold || isShowRolloverChg || isShowBulkEditPanel) && memoRemark1}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(isShowStackHold || isShowRolloverChg) && memoRemark2}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(isShowGenWaiverByCom || isShowBulkEditPanel) && memoReasonForRecommendation}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {memoWaiverPercentage}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {(operatingUnit !== "SAPT" && operatingUnit !== "KICT") && memoWaiverType}
    </CriteriaItemContainer>
    <CriteriaItemContainer>
      {isShowGenWaiverByCom && memoRemark}
    </CriteriaItemContainer>

  </>;
}
